import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, CircularProgress, Alert
} from '@mui/material';
import axios from 'axios';
import { useNotification } from '../context/NotificationContext';

import { API, getAuthHeader } from './customerApp/shared';

/**
 * Provider referral program (owners only): every referred SP, their progress
 * toward the completed-jobs threshold, and bonus status. Payment is manual —
 * "Mark paid" records it here after you pay the referrer.
 */
function Referrals() {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/referrals`, { headers: getAuthHeader() });
      setRows(data.referrals || []);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to load referrals', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (userId) => {
    try {
      await axios.post(`${API}/api/referrals/${userId}/mark-paid`, {}, { headers: getAuthHeader() });
      showNotification('Referral bonus marked paid', 'success');
      load();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to mark paid', 'error');
    }
  };

  const runSweep = async () => {
    try {
      const { data } = await axios.post(`${API}/api/referrals/sweep`, {}, { headers: getAuthHeader() });
      showNotification(`Checked ${data.checked} pending referral(s); ${data.qualified} newly qualified`, 'success');
      load();
    } catch (err) {
      showNotification('Sweep failed', 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Provider Referrals</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Providers enter the referring company's vendor number on their application.
        When a referred provider completes their first {rows[0]?.threshold ?? 5} jobs,
        the referral qualifies and you pay the referrer their bonus (checked
        automatically once a day, or on demand below).
      </Typography>

      <Button variant="outlined" size="small" onClick={runSweep} sx={{ mb: 2 }}>
        Check qualifications now
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">
          No referred providers yet. Share the pitch: "Refer a towing company —
          ${rows[0]?.bonusAmount ?? 100} after their first 5 completed jobs."
        </Alert>
      ) : (
        <Paper sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Referred Company</TableCell>
                <TableCell>Referred By</TableCell>
                <TableCell align="center">Completed Jobs</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Bonus</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.userId}>
                  <TableCell>{r.company || r.vendorNumber}</TableCell>
                  <TableCell>{r.referrerCompany}</TableCell>
                  <TableCell align="center">{r.completedJobs} / {r.threshold}</TableCell>
                  <TableCell align="center">
                    {r.paid ? (
                      <Chip label={`Paid ${new Date(r.paidAt).toLocaleDateString()}`} color="success" size="small" />
                    ) : r.qualified ? (
                      <Chip label="Qualified — pay bonus" color="warning" size="small" />
                    ) : (
                      <Chip label="In progress" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">${Number(r.bonusAmount).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    {r.qualified && !r.paid && (
                      <Button size="small" variant="contained" onClick={() => markPaid(r.userId)}>
                        Mark paid
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

export default Referrals;
